import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import countries from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";
import { StatusBadge } from "../dashboard/DashboardUI";
import { DocumentList, RequestSteps } from "./RequestDetailModal";
import { getBusinessCreationRequestDocuments } from "../../redux/actions/DocumentActions";
import {
  procedureLabel,
  requestTitle,
  serviceLabel,
} from "../../utils/requestDisplay";
import { ACTIVITY_SECTORS } from "../../utils/requestDetails";
import { isAssociation } from "../../utils/creationFlow";
import "../dashboard/dashboard.css";
import { seatAddress } from "../../utils/regions";

countries.registerLocale(fr);

const CREATION_DOCUMENTS = {
  mgId: "Pièce d'identité du dirigeant",
  mgCrimRecord: "Casier judiciaire du dirigeant",
  mgWedCert: "Acte de mariage du dirigeant",
  shdIds: "Pièces d'identité des associés",
  capDepProof: "Attestation de dépôt du capital",
  verbalReport: "Procès-verbal",
  location: "Plan de localisation",
  assocStatutes: "Statuts de l'association",
  assocRules: "Règlement intérieur",
  assocAssemblyReport: "Procès-verbal de l'assemblée constitutive",
};

const DetailsList = ({ rows }) => (
  <dl className="dash-details">
    {rows
      .filter(([, value]) => value)
      .map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
  </dl>
);

// Detail of a company creation request (client and admin spaces)
function BusinessCreationModal({ show, onHide, businessCreation }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState(null);

  useEffect(() => {
    if (!businessCreation?.reference || !user?.token) return;

    setLoading(true);
    Promise.resolve(
      dispatch(
        getBusinessCreationRequestDocuments(
          user.token,
          businessCreation.reference,
          (data) => setDocuments(data || null),
        ),
      ),
    ).finally(() => setLoading(false));
  }, [businessCreation?.reference, user?.token, dispatch]);

  if (!businessCreation) return null;

  const request = businessCreation;
  const association = isAssociation(request.businessType);
  const services = (request.services || []).filter(
    (service) => service.code !== "registration",
  );
  const files = Object.entries(CREATION_DOCUMENTS)
    .filter(([key]) => documents?.[key]?.url)
    .map(([key, label]) => ({ key, label, file: documents[key] }));

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <div className="dash-modal-head">
          <Modal.Title as="h2" className="dash-card-title mb-0">
            {requestTitle(request)}
          </Modal.Title>
          <span className="dash-cell-sub">
            #{request.reference} ·{" "}
            {moment(request.createdAt).format("DD/MM/YYYY")}
          </span>
        </div>
        <StatusBadge status={request.status} />
      </Modal.Header>

      <Modal.Body>
        <RequestSteps status={request.status} />

        <h3 className="dash-subtitle">
          {association ? "L'association" : "L'entreprise"}
        </h3>
        <DetailsList
          rows={[
            [association ? "Délai" : "Procédure", procedureLabel(request)],
            [
              "Nom",
              request.businessName ||
                "À fournir par Platform98 selon votre commande",
            ],
            ["Forme juridique", request.businessType],
            [
              "Secteur d'activité",
              !association &&
                (ACTIVITY_SECTORS[request.activitySector] ||
                  request.activitySector),
            ],
            [
              "Capital social",
              request.shareCapital > 0 &&
                `${request.shareCapital.toLocaleString("fr-FR")} FCFA`,
            ],
            [association ? "Président" : "Gérant", request.managerName],
            ["Siège social", seatAddress(request)],
            [
              "Pays",
              countries.getName(request.country, "fr") || request.country,
            ],
            ["Téléphone", request.companyPhone],
            ["E-mail", request.companyEmail],
            ["Banque", request.bank],
          ]}
        />

        {services.length > 0 && (
          <>
            <h3 className="dash-subtitle">Services associés</h3>
            <ul className="dash-plain-list">
              {services.map((service, index) => (
                <li key={index}>{serviceLabel(service)}</li>
              ))}
            </ul>
          </>
        )}

        {loading ? (
          <p className="dash-loading">Chargement des documents…</p>
        ) : (
          <DocumentList documents={files} />
        )}

        {request.comment && (
          <div className="dash-note">
            <h3 className="dash-subtitle">Message de nos équipes</h3>
            <p className="dash-pre">{request.comment}</p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default BusinessCreationModal;
